import React from "react";
import { Link } from "react-router-dom";
import { Container, SectionLabel } from "../components/ui-kit";
import PageHero from "../components/PageHero";
import { Target, Eye, Compass, Building2, ArrowRight } from "lucide-react";
import OneUmmahMap from "../components/OneUmmahMap";

const img = (id) => `https://images.unsplash.com/photo-${id}?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200`;

const MOSQUE_ROLES = [
  ["Places of worship", "Where communities turn to Allah in prayer and reflection."],
  ["Centres of community", "Gathering points that hold neighbourhoods together."],
  ["Spaces of education", "Where knowledge and the Qur'an are taught across generations."],
  ["Symbols of heritage", "Living landmarks of Islamic history and identity."],
];

export default function About() {
  return (
    <div>
      <PageHero
        label="About Helping Hands Dubai"
        title="The world is connected. Compassion should be too."
        subtitle="Communities are separated by geography, borders and circumstance — but human responsibility does not stop at borders. Helping Hands Dubai exists to connect generosity with organisation, technology, trusted partnerships and measurable impact."
        image={img("1584758876461-054ae11c366d")}
        crumbs={[{ label: "About Us" }]}
      />

      <section id="story" className="scroll-mt-28 bg-white">
        <Container className="grid gap-12 py-16 lg:grid-cols-2">
          <div>
            <SectionLabel>Our Story</SectionLabel>
            <h2 className="mt-4 font-display text-4xl font-bold text-emerald-900">More than generosity — organised, accountable impact</h2>
          </div>
          <div className="space-y-4 text-lg leading-relaxed text-slate-600">
            <p>Millions of people want to contribute to something meaningful — to help communities, preserve places of worship, support families, educate children and create opportunity. The challenge is rarely a lack of generosity.</p>
            <p>The real challenge is connecting that generosity with organisation, technology, trusted partnerships, accountability and measurable impact. This is where Helping Hands Dubai exists — as a technology-enabled CSR and social-impact platform serving communities across continents, with a deep commitment to the global Muslim ummah.</p>
          </div>
        </Container>
      </section>

      <section className="bg-sand">
        <Container className="py-16">
          <div className="grid gap-6 md:grid-cols-3">
            {[
              { id: "vision", icon: Eye, title: "Our Vision", text: "To build a world where technology makes responsible contribution, community support and social impact more accessible, transparent and meaningful." },
              { id: "mission", icon: Target, title: "Our Mission", text: "To connect people, businesses, CSR teams and implementation partners with initiatives that create tangible and lasting community impact." },
              { id: "governance", icon: Compass, title: "Our Approach", text: "We identify needs, verify partners, approve programs, process contributions responsibly, implement, and document measurable impact." },
            ].map((v) => (
              <div key={v.id} id={v.id} className="scroll-mt-28 rounded-2xl border border-emerald-900/10 bg-white p-7">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-900 text-gold-light"><v.icon className="h-5 w-5" /></div>
                <h3 className="mt-4 font-display text-xl font-bold text-emerald-900">{v.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{v.text}</p>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* ONE UMMAH — interactive region map */}
      <OneUmmahMap />

      {/* PRESERVING THE HOUSES OF ALLAH */}
      <section className="bg-white">
        <Container className="grid items-center gap-12 py-16 lg:grid-cols-2">
          <div className="relative">
            <img src={img("1581141444721-0e6f8fa8397e")} alt="Mosque" className="rounded-3xl object-cover hh-shadow-lg" />
          </div>
          <div>
            <SectionLabel>Our Flagship Focus</SectionLabel>
            <h2 className="mt-4 font-display text-4xl font-bold text-emerald-900">Preserving the houses of Allah</h2>
            <p className="mt-4 text-lg leading-relaxed text-slate-600">Across generations, mosques have been more than places of prayer. Our mosque restoration and community-infrastructure initiatives help preserve these spaces — supporting authorised projects across different regions of the world.</p>
            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              {MOSQUE_ROLES.map(([t, d]) => (
                <div key={t} className="rounded-xl border border-emerald-900/10 bg-sand p-4">
                  <div className="flex items-center gap-2 text-sm font-bold text-emerald-900"><Building2 className="h-4 w-4 text-gold" /> {t}</div>
                  <p className="mt-1 text-sm text-slate-600">{d}</p>
                </div>
              ))}
            </div>
            <Link to="/programs/community-development" className="mt-7 inline-flex items-center gap-2 rounded-full bg-emerald-900 px-7 py-3.5 text-sm font-bold text-white transition hover:bg-emerald-800">Explore Mosque Restoration <ArrowRight className="h-4 w-4 rtl:rotate-180" /></Link>
          </div>
        </Container>
      </section>
    </div>
  );
}
